import { useEffect, useState } from "react";
import { useChatContext } from "stream-chat-react";
import { useConversationByContact } from "views/inbox/api/use-conversation-by-contact";
import type { ApplicantOrInvitee } from "../types";

// Unread chat count for an applicant card: the applicant's native conversation
// when the partner is on native_chat, otherwise the legacy Stream channel.
export const useApplicantChatUnreads = ({
  applicant,
  hasChat,
  isNativeChat
}: {
  applicant: ApplicantOrInvitee;
  hasChat: boolean;
  isNativeChat: boolean;
}): number => {
  const { client } = useChatContext();
  const isApplicantWithChat = "verifications" in applicant && hasChat;
  const [streamUnreads, setStreamUnreads] = useState(0);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- client can be undefined at runtime outside <Chat>
    if (isNativeChat || !isApplicantWithChat || !client) return;

    let cancelled = false;
    const channel = client.channel("messaging", `chat-id-${applicant.id}`);

    channel
      .query()
      .then(() => {
        if (!cancelled) setStreamUnreads(channel.countUnread());
      })
      .catch(() => {});

    const subscription = client.on((event) => {
      if (!event.cid || event.cid !== channel.cid) return;

      if (event.type === "notification.message_new") {
        channel
          .query()
          .then(() => {
            if (!cancelled) setStreamUnreads(channel.countUnread());
          })
          .catch(() => {});
        return;
      }

      setStreamUnreads(channel.countUnread());
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [isNativeChat, isApplicantWithChat, client, applicant.id]);

  const nativeContactId =
    isNativeChat && "verifications" in applicant
      ? applicant.crm_contact_id
      : undefined;
  const { data: nativeConversation } = useConversationByContact(
    nativeContactId,
    { refetchInterval: 15_000 }
  );

  return isNativeChat
    ? (nativeConversation?.conversation.unread_count ?? 0)
    : streamUnreads;
};
